import landscapeCarousel from 'components/landscapeCarousel';
import spotlightTpl from 'templates/carousels/__carouselSpotlight.tpl';
import { SpotlightDefaultBreakpointRules } from 'components/settings/BreakpointSettings';
import Countdown from 'components/countdown';
import WatchlistCta from '../components/watchlist/WatchlistCta';
import PreferencesCta from '../components/preferences/PreferencesCta';
import { trackAction } from 'helpers/trackingOM';
import { formatSpotlightTime } from 'helpers/dates';
import appendSpotlightTracking from './helpers/spotlightTracking';
import { KIDS, YOUNGER_KIDS } from 'helpers/ProfileConstants';
import { supportsUnmutedAutoplay, VideoPreview } from 'components/videoPreview/videoPreview';
import { VideoPreviewEvent } from '../components/videoPreview/videoPreviewConstants';

export default function spotlightCarousel($wrapper, carouselData) {
    let $spotlightWrapperElement = null;
    let spotlightWrapperElement = null;
    let $carouselElement = $wrapper.find('.js-video-carousel');
    let isExpandedCarouselVideoPlaying = false;
    let videoHasEnded = false;
    let videoHasStarted = false;

    const isKidsProfile =
        CBS?.Registry?.user?.profile_type == KIDS ||
        CBS?.Registry?.user?.profile_type == YOUNGER_KIDS;

    try {
        const spotlightCarousel = {
            wrapper: $carouselElement,
            init: function () {
                let options = {
                    endPoint: carouselData.baseEndpoint,
                    template: spotlightTpl,
                    responsive: SpotlightDefaultBreakpointRules,
                    params: {
                        slug: $wrapper.data('slug'),
                        sectionId: $wrapper.data('sectionid'),
                    },
                };
                landscapeCarousel({
                    parentContainer: $carouselElement,
                    options: options,
                });
                $wrapper.on('init', () => spotlightWrapperInit());
            },
        };
        return spotlightCarousel;
    } catch (e) {
        console.log(e);
    }

    function spotlightWrapperInit() {
        $carouselElement.removeClass('hidden');
        let observer = new MutationObserver(function (mutations) {
            $spotlightWrapperElement = $wrapper.find('.spotlight-carousel-wrapper');
            if ($spotlightWrapperElement?.length) {
                spotlightWrapperElement = $spotlightWrapperElement[0];
                initCountdown();
                initWatchListCTA();
                initPreferencesCTA();
                initDate();
                initBackgroundVideo();
                initWatchNowCTAClick();
                showCarousel();
                observer.disconnect();
            }
        });
        observer.observe(document, {
            attributes: false,
            childList: true,
            characterData: false,
            subtree: true,
        });
    }

    async function handleCarouselVideoStart(player, spotlightBackgroundVideo) {
        isExpandedCarouselVideoPlaying = true;
        if (spotlightBackgroundVideo?.classList.contains('show')) {
            try {
                await player?.pause();
            } catch (e) {
                console.error('Spotlight video pause() error', e);
            }
        }
    }

    async function handleCarouselVideoStop(player, spotlightBackgroundVideo, muteToggleButton) {
        isExpandedCarouselVideoPlaying = false;
        if (spotlightBackgroundVideo?.classList.contains('show')) {
            setVideoMuteState(player, muteToggleButton);
            try {
                await player?.play();
            } catch (e) {
                console.error('Spotlight video play() error', e);
            }
        }
    }

    function initCountdown() {
        let $countdownElement = $spotlightWrapperElement.find('.spotlight-countdown');
        if ($countdownElement.length > 0) {
            new Countdown($countdownElement[0]);
        }
    }

    function initWatchListCTA() {
        let $watchListCTAElement = $spotlightWrapperElement.find('.watchlist_wrapper');
        const watchlistEnabled = CBS?.Registry?.Features?.watchlist_enabled;
        if (watchlistEnabled && $watchListCTAElement.length > 0) {
            try {
                new WatchlistCta($spotlightWrapperElement[0]);
            } catch (e) {
                console.error('Watchlist cta failed to load', e);
            }
        }
    }

    function initPreferencesCTA() {
        let $preferenceCTAElement = $spotlightWrapperElement.find('.content_preference_wrapper');
        const preferencesEnabled =
            CBS?.Registry?.Features?.content_preferences_enabled && !isKidsProfile;
        if (preferencesEnabled && $preferenceCTAElement.length > 0) {
            try {
                new PreferencesCta($spotlightWrapperElement[0]);
            } catch (e) {
                console.error('Preferences cta failed to load', e);
            }
        }
    }

    function initDate() {
        let $airDate = $spotlightWrapperElement.find('.air-date');
        if ($airDate.length > 0) {
            let streamStartDate = $airDate.data('stream-start-timestamp');
            let streamEndDate = $airDate.data('stream-end-timestamp');
            let startDate = $airDate.data('start-timestamp');
            let endDate = $airDate.data('end-timestamp');
            if (
                streamStartDate &&
                streamEndDate &&
                startDate &&
                endDate &&
                typeof formatSpotlightTime === 'function'
            ) {
                $airDate.html(
                    formatSpotlightTime(streamStartDate, streamEndDate, startDate, endDate),
                );
            }
        }
    }

    function showSpotlightBackgroundVideo(spotlightBackgroundVideo, muteToggleButton) {
        spotlightBackgroundVideo?.classList.add('show');
        muteToggleButton?.classList.add('show');

        // pause hero videos (which should be paused already but sometimes aren't)
        const heroVideos = document.querySelectorAll('.marqueeVideoContainer video');
        if (heroVideos) {
            try {
                heroVideos.forEach((heroVideo) => {
                    heroVideo.pause();
                });
            } catch (e) {
                console.error('Spotlight error pausing hero videos', e);
            }
        }
    }

    function hideSpotlightBackgroundVideo(spotlightBackgroundVideo, muteToggleButton) {
        spotlightBackgroundVideo?.classList.remove('show');
        muteToggleButton?.classList.remove('show');
    }

    function setVideoMuteState(player, muteToggleButton) {
        const spliceVolumeOn = localStorage.getItem('spliceVolumeOn');
        try {
            if (spliceVolumeOn === '1') {
                player?.mute(false);
                muteToggleButton?.classList.remove('muted');
                muteToggleButton?.setAttribute('title', 'Mute Audio');
            } else {
                player?.mute(true);
                muteToggleButton?.classList.add('muted');
                muteToggleButton?.setAttribute('title', 'Unmute Audio');
            }
        } catch (e) {
            console.error('Spotlight video mute() error', e);
        }
    }

    async function initBackgroundVideo() {
        let videoPreviewUrl = spotlightWrapperElement?.dataset.videoPreviewUrl;
        const spotlightBackgroundVideo = spotlightWrapperElement?.querySelector(
            '.spotlight-background-video',
        );
        if (!videoPreviewUrl || !spotlightBackgroundVideo) {
            return;
        }
        const muteToggleButton = spotlightWrapperElement.querySelector('.hero-volume');

        const spliceVolumeOn = localStorage.getItem('spliceVolumeOn');
        let unmutedAutoplay = false;
        try {
            unmutedAutoplay = spliceVolumeOn === '1' && (await supportsUnmutedAutoplay());
        } catch (e) {
            console.error('spotlight autoplay capability error', e);
        }

        const player = new VideoPreview({
            id: `spotlight-player-${carouselData.carouselId}`,
            selector: `#spotlight-player-${carouselData.carouselId}`,
            mediaUrl: videoPreviewUrl,
            delayStart: 0,
            destroyAfterPlay: false,
            mutedAutoplay: !unmutedAutoplay,
        });

        let intersectionObserverOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 1,
        };
        let playTimeoutId = null;
        let hasPlayed = false;
        player?.addEventListener(VideoPreviewEvent.ENDED, (e) => {
            hideSpotlightBackgroundVideo(spotlightBackgroundVideo, muteToggleButton);
            trackVideoPreview('trackVideoPreviewEnd', e.detail?.contentTime);
            videoHasEnded = true;
        });

        document.body.addEventListener('carouselVideoStart', () => {
            handleCarouselVideoStart(player, spotlightBackgroundVideo);
        });
        document.body.addEventListener('carouselVideoStop', () => {
            handleCarouselVideoStop(player, spotlightBackgroundVideo, muteToggleButton);
        });

        let observer = new IntersectionObserver((entries) => {
            entries.forEach(async (entry) => {
                // spotlight in viewport
                if (entry.isIntersecting && !isExpandedCarouselVideoPlaying) {
                    playTimeoutId = setTimeout(async () => {
                        try {
                            if (!videoHasStarted) {
                                await player.setPlayer();
                                videoHasStarted = true;
                            } else if (videoHasEnded) {
                                await player.replay();
                                videoHasEnded = false;
                            } else {
                                player.resumePlayback();
                            }
                        } catch (error) {
                            console.log('Spotlight video play/resume error', error);
                        }
                        playTimeoutId = null;
                        if (!player.playerExists()) {
                            return;
                        }
                        showSpotlightBackgroundVideo(spotlightBackgroundVideo, muteToggleButton);
                        setVideoMuteState(player, muteToggleButton);
                        trackVideoPreview('trackVideoPreviewStart');
                        hasPlayed = true;
                    }, 1000);
                } else {
                    if (hasPlayed) {
                        hideSpotlightBackgroundVideo(spotlightBackgroundVideo, muteToggleButton);
                        clearTimeout(playTimeoutId);
                        if (!videoHasEnded) {
                            trackVideoPreview('trackVideoPreviewEnd');
                        }
                        hasPlayed = false;
                        player.suspendPlayback();
                    } else if (playTimeoutId) {
                        clearTimeout(playTimeoutId);
                        playTimeoutId = null;
                    }
                }
            });
        }, intersectionObserverOptions);
        observer.observe(spotlightBackgroundVideo);
        initMuteToggle(player, muteToggleButton);
    }

    async function initMuteToggle(player, muteToggleButton) {
        if (!player || !muteToggleButton) {
            return;
        }
        muteToggleButton.addEventListener('click', async (ev) => {
            let currentTarget = ev.currentTarget;
            try {
                if (currentTarget.classList.contains('muted')) {
                    player.mute(false);
                    currentTarget.classList.remove('muted');
                    currentTarget.setAttribute('title', 'Mute Audio');
                    localStorage.setItem('spliceVolumeOn', 1);
                } else {
                    player.mute(true);
                    currentTarget.classList.add('muted');
                    currentTarget.setAttribute('title', 'Unmute Audio');
                    localStorage.setItem('spliceVolumeOn', 0);
                }
            } catch (e) {
                console.error('Spotlight video mute() error', e);
            }
            if (player.isPaused()) {
                try {
                    await player.play();
                } catch (e) {
                    console.error('Spotlight video play() error', e);
                }
            }
        });
    }

    function initWatchNowCTAClick() {
        let $watchNowCTA = $spotlightWrapperElement.find('.spotlight-button--live');
        // if empty href, reach out to api again and check for actionUrl
        if ($watchNowCTA.length > 0 && $watchNowCTA.attr('href') == '') {
            $watchNowCTA.on('click', (e) => {
                e.preventDefault();
                $.ajax({
                    url: carouselEndpoint,
                    type: 'POST',
                    dataType: 'json',
                    success: function (resp) {
                        // redirect to the latest actionUrl
                        if (resp?.result?.data[0]?.actionUrl) {
                            redirecctToUrl(resp.result.data[0].actionUrl);
                        } else {
                            redirectToDetailsPage();
                        }
                    },
                    error: function () {
                        redirectToDetailsPage();
                    },
                });
            });
        }
    }

    function redirecctToUrl(url) {
        if (url) {
            window.location.href = url;
        }
    }

    function redirectToDetailsPage() {
        // redirect to existing details page url
        let detailsCtaHref = $spotlightWrapperElement
            .find('.spotlight-button--details')
            .attr('href');
        if (detailsCtaHref) {
            redirecctToUrl(detailsCtaHref);
        }
    }

    function showCarousel() {
        setTimeout(function () {
            $spotlightWrapperElement.addClass('show');
        }, 500);
    }

    function trackVideoPreview(action, contentTime = null) {
        // extra params for spotlight carousel
        let aaLinkData = $spotlightWrapperElement
            .find('.spotlight-link')
            .attr('aa-link')
            .split('|');
        let video = spotlightWrapperElement?.querySelector('.spotlight-background-video video');
        let $countdown = $spotlightWrapperElement.find('.spotlight-countdown');
        let currentTime = contentTime ?? video?.currentTime;
        let elapsedTime = Math.trunc(currentTime).toString() ?? '0';
        let trackingObj = {
            videoPreviewId: aaLinkData[18] ?? '',
            elapsedTime: elapsedTime ?? '',
            streamStartTimestamp: $countdown.data('stream-start-timestamp') ?? '',
            streamEndTimestamp: $countdown.data('stream-end-timestamp') ?? '',
            contentLocked: aaLinkData[17],
        };
        trackingObj = appendSpotlightTracking($wrapper, trackingObj);
        trackAction(action, trackingObj);
    }
}
